/**
 * LoginController
 *
 * There are 3 scenarios:
 * 1. / or /login is handled without any branding
 * 2. /brandname will look up to see if there is a brand
 * 3. query string ?redirect is checked to see if there is a site page to redirect back to
 *    If detected will fetch site assets to show.
 */
import React from "react";
import { connect } from "react-redux";
import me from "models/me";
import * as Loading from "components/loading";
import PageNotFound from "containers/page-not-found";
import Page from "components/page";
import NuorderFooter from "components/login/nuorder-footer";
import LoginForm from "components/login/login-form";
import LoginFormSSO from "components/login/login-form-sso";
import { merge, $ } from "glamor";
import { Branding } from "components/login/branding";
import redirectOnLogin from "helpers/redirectOnLogin";
import { formatMessage } from "lib/globalize-util";
import {
    requestForgotPassword,
    resetState,
    trackLoginArticles,
    trackLoginRegistration,
    trackLoginVideo,
} from "actions/authentication";
import { fetchBrandedLogin, displaySpinner } from "actions/scope";
import { withRouter } from "react-router";
import { IState as IAuth } from "reducers/authentication";
import { IState as IBrandedLoginState } from "reducers/branded-login";
import { useFetchSiteBySlug } from "containers/sites-manager/hooks/sites";
import { getLsidConfig } from "lib/lsid";
import { FlowTypes } from "lib/lsid/enums";

interface IStateProps {
    authentication: IAuth;
    brandedLoginEntities: any;
    brandedLoginState: IBrandedLoginState;
}

interface IDispatchProps {
    onRequestForgotPassword: ({ email, recaptchaClientKey }) => void;
    onResetState: () => void;
    onFetchBrandedLogin: (slug: string) => void;
    onDisplaySpinner: (force: boolean) => void;
    onTrackLoginArticles: () => void;
    onTrackLoginRegistration: () => void;
    onTrackLoginVideo: () => void;
}

interface IOwnProps {}

type IProps = IOwnProps & IDispatchProps & IStateProps & { router: any; location: any };

const isCustomLoginPath = (pathname: string) => !["/", "/login"].includes(pathname);

const LoginContainer: React.FC<IProps> = ({
    location,
    router,
    authentication,
    brandedLoginState,
    brandedLoginEntities,
    onFetchBrandedLogin,
    onRequestForgotPassword,
    onDisplaySpinner,
    onResetState,
    onTrackLoginArticles,
    onTrackLoginRegistration,
    onTrackLoginVideo,
}) => {
    const { redirect = "", error } = location.query;
    const slug = location.pathname.replace("/", "");
    const isCustomLoginPage = isCustomLoginPath(location.pathname);
    const { fetching, show404 } = brandedLoginState;
    const model = brandedLoginEntities ? brandedLoginEntities[slug] : null;
    const [displayHelpFooter, setDisplayHelpFooter] = React.useState<boolean>(false);
    const [loginFlow, setLoginFlow] = React.useState<FlowTypes>(FlowTypes.LEGACY);
    const [sharedEmail, setSharedEmail] = React.useState<string>("");
    const { hasSSO } = getLsidConfig();

    const styles = React.useMemo(
        () =>
            merge(
                {
                    position: "absolute",
                    top: "0px",
                    left: "0px",
                    bottom: "0px",
                    right: "0px",
                    background: ["#fff", "linear-gradient(180deg, #fff, #f8f8f8)"],
                },
                $(" .loginFormWrapper", {
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 5,
                }),
            ),
        [],
    );

    const [resourceType = "", resourceId = ""] = redirect.replace(/^\/+/, "").split("/");
    const {
        data: site,
        isFetching: isSiteFetching,
        isError: isSiteError,
    } = useFetchSiteBySlug(resourceId, {
        enabled: !isCustomLoginPage && resourceType === "site" && resourceId.length,
        retry: false,
    });

    const innerWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    const [siteBackgrounds, siteLogo, siteRegURL] = React.useMemo(
        () => [
            site?.hero_image?.url ? [`${site.hero_image.url}?max_width=${innerWidth}`] : undefined,
            site?.logo_image?.url ? `${site.logo_image.url}?max_width=340` : undefined,
            site?.buyer_registration_url ?? undefined,
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [site],
    );

    const handleRedirectToForgotPassword = () => router.push("forgot-password");

    React.useEffect(() => {
        if ((site || isSiteError) && isSiteFetching === false) {
            onDisplaySpinner(false);
        }
    }, [isSiteFetching]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (me.isSignedInFully()) {
            redirectOnLogin(router);
            window.location.reload();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (isCustomLoginPage) {
            onFetchBrandedLogin(slug);
        } else {
            const [resourceType = "", resourceId = ""] = redirect.replace(/^\/+/, "").split("/");
            if (resourceType !== "site" || !resourceId.length) {
                onDisplaySpinner(false);
            }
        }
    }, [slug, isCustomLoginPage, redirect]); // eslint-disable-line react-hooks/exhaustive-deps

    // If branded login is not there, redirect back
    React.useEffect(() => {
        if (brandedLoginState.error?.status === 308 && isCustomLoginPage) {
            router.replace("");
        }
    }, [brandedLoginState.error, isCustomLoginPage]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (authentication.forgotPasswordSuccess) {
            setDisplayHelpFooter(true);
        }
    }, [authentication.forgotPasswordSuccess]);

    if (fetching || isSiteFetching) {
        return <Loading.default />;
    } else if (show404) {
        return <PageNotFound />;
    }

    return (
        <Page title={formatMessage("Please Login")} hasPageHeader={false}>
            <div {...styles}>
                {(model || siteBackgrounds) && <Branding model={model} images={siteBackgrounds || model.images} />}
                <div className="loginFormWrapper">
                    {!(hasSSO && loginFlow === FlowTypes.SSO) ? (
                        <LoginForm
                            error={error && formatMessage(error)}
                            authentication={authentication}
                            model={model}
                            siteLogo={siteLogo}
                            siteRegURL={siteRegURL}
                            redirect={redirect}
                            onRequestForgotPassword={onRequestForgotPassword}
                            onResetState={onResetState}
                            onSetDisplayHelpFooter={setDisplayHelpFooter}
                            onTrackLoginArticles={onTrackLoginArticles}
                            onTrackLoginRegistration={onTrackLoginRegistration}
                            onTrackLoginVideo={onTrackLoginVideo}
                            onRedirectToForgotPassword={handleRedirectToForgotPassword}
                            onSwitchLoginFlow={setLoginFlow}
                            setSharedEmail={setSharedEmail}
                            sharedEmail={sharedEmail}
                        />
                    ) : (
                        <LoginFormSSO
                            error={error && formatMessage(error)}
                            authentication={authentication}
                            model={model}
                            siteLogo={siteLogo}
                            siteRegURL={siteRegURL}
                            redirect={redirect}
                            onResetState={onResetState}
                            onSwitchLoginFlow={setLoginFlow}
                            setSharedEmail={setSharedEmail}
                            sharedEmail={sharedEmail}
                            isMobile={false}
                        />
                    )}
                </div>
                <NuorderFooter displayHelpFooter={displayHelpFooter} />
            </div>
        </Page>
    );
};

const mapStateToProps = (state) => {
    return {
        authentication: state.authentication,
        brandedLoginEntities: state.entities.brandedLogin,
        brandedLoginState: state.brandedLogin,
    };
};

function mapDispatchToProps(dispatch) {
    return {
        onRequestForgotPassword: ({ email, recaptchaClientKey }) =>
            dispatch(requestForgotPassword({ email, recaptchaClientKey })),
        onResetState: () => dispatch(resetState()),
        onFetchBrandedLogin: (slug: string) => dispatch(fetchBrandedLogin(slug)),
        onDisplaySpinner: (force: boolean) => dispatch(displaySpinner(force)),
        onTrackLoginArticles: () => dispatch(trackLoginArticles()),
        onTrackLoginRegistration: () => dispatch(trackLoginRegistration()),
        onTrackLoginVideo: () => dispatch(trackLoginVideo()),
    };
}

export const Login = withRouter(
    connect<IStateProps, IDispatchProps, IOwnProps>(mapStateToProps, mapDispatchToProps)(LoginContainer),
);
export default Login;
